import { Location } from "@/types/place"
import { BoundingBox } from "@/types/util"

import { AmplitudeEmptyValue } from "@/lib/analytics/amplitude-types"

export function distanceFromLocation(
  location: Location,
  latitude: number,
  longitude: number
): number | undefined {
  if (location.latitude == null || location.longitude == null) {
    return undefined
  }
  return distanceBetweenLocations(
    location.latitude,
    location.longitude,
    latitude,
    longitude
  )
}

// Ad click
export function getPageAdClickIds(pathName: string) {
  const queryParams = new URLSearchParams(pathName.split("?")[1])

  return {
    gclid: queryParams.get("gclid") || AmplitudeEmptyValue,
    fbclid: queryParams.get("fbclid") || AmplitudeEmptyValue,
    ttclid: queryParams.get("ttclid") || AmplitudeEmptyValue,
  }
}

// haversine distance
export function distanceBetweenLocations(
  lat1: number,
  long1: number,
  lat2: number,
  long2: number
): number {
  const R = 6371e3 // metres
  const φ1 = (lat1 * Math.PI) / 180 // φ, λ in radians
  const φ2 = (lat2 * Math.PI) / 180
  const Δφ = ((lat2 - lat1) * Math.PI) / 180
  const Δλ = ((long2 - long1) * Math.PI) / 180

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

  const d = R * c
  return d
}

export function convertDistance(
  distanceInMeters: number,
  unitSystem: "metric" | "imperial"
): number {
  if (unitSystem === "imperial") {
    return distanceInMeters / 1609.34
  }
  return distanceInMeters / 1000
}

export function convertKmToMi(km: number) {
  return km * 0.621371
}

// cartesian product of two arrays
export function product<T, U>(array1: T[], array2: U[]): [T, U][] {
  const cartesianProduct: [T, U][] = []

  for (let i = 0; i < array1.length; i++) {
    for (let j = 0; j < array2.length; j++) {
      cartesianProduct.push([array1[i], array2[j]])
    }
  }
  return cartesianProduct
}

export const arraysEqual = <T>(
  arr1: T[],
  arr2: T[],
  comparator?: (a: T, b: T) => number
): boolean => {
  if (arr1.length !== arr2.length) return false

  const sortedArr1 = [...arr1].sort(comparator)
  const sortedArr2 = [...arr2].sort(comparator)

  for (let i = 0; i < sortedArr1.length; i++) {
    if (sortedArr1[i] !== sortedArr2[i]) return false
  }

  return true
}

export function findNearest<T>(
  list: T[],
  distance: (element: T) => number
): number {
  let closestIndex = -1
  let closestDistance = Number.MAX_SAFE_INTEGER
  for (let i = 0; i < list.length; i++) {
    const computedDistance = distance(list[i])
    if (computedDistance < 0) {
      continue
    } else if (
      computedDistance < closestDistance ||
      closestDistance === Number.MAX_SAFE_INTEGER
    ) {
      closestDistance = computedDistance
      closestIndex = i
    }
  }
  return closestIndex
}

export function isCoordWithinBbox(
  coord: { lat: number; lng: number },
  bbox: BoundingBox
) {
  const [west, south, east, north] = bbox
  return (
    coord.lat <= north &&
    coord.lat >= south &&
    coord.lng <= east &&
    coord.lng >= west
  )
}
